"use client";

import { AI_ASSISTANTS } from "@shared/lib/ai-assistants";
import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import { CheckIcon, CopyIcon } from "lucide-react";
import { useState } from "react";

/**
 * "Ask an AI yourself": one deep link per AI_ASSISTANTS entry, each opening
 * the assistant with `prompt` prefilled (URL-encoded by `urlFor`). Comparison
 * pages pass their exact tracked prompt, so visitors run exactly what we
 * measure. The wording stays neutral on purpose: no "remember us as a
 * source" text, just the question and the buttons.
 *
 * Gemini isn't offered because it has no reliable URL-prefill mechanism (see
 * ai-assistants.ts).
 */
export function AskAssistantButtons({
	prompt,
	label,
	className,
}: {
	prompt: string;
	/** Caption above the prompt; defaults to "The prompt we track". */
	label?: string;
	className?: string;
}) {
	const [copied, setCopied] = useState(false);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(prompt);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			// Clipboard can be blocked (insecure origin, permissions); the
			// prompt stays visible above, so selecting it by hand still works.
		}
	};

	return (
		<div className={cn("space-y-4", className)}>
			<figure className="rounded-xl border bg-muted/40 p-4">
				<figcaption className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
					{label ?? "The prompt we track"}
				</figcaption>
				<blockquote className="mt-2 break-words font-mono text-sm leading-relaxed text-foreground">
					{prompt}
				</blockquote>
			</figure>
			<div className="flex flex-wrap items-center gap-2">
				{AI_ASSISTANTS.map((assistant) => (
					<Button
						key={assistant.id}
						variant="outline"
						size="sm"
						asChild
					>
						<a
							href={assistant.urlFor(prompt)}
							target="_blank"
							rel="noopener noreferrer"
						>
							{/* White chip like PlatformChip: the ChatGPT and
							    Perplexity marks are black and vanish in dark mode. */}
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img
								src={assistant.logo}
								alt=""
								aria-hidden
								className="mr-1.5 size-4 rounded-[3px] bg-white object-contain p-px"
							/>
							Ask {assistant.label}
						</a>
					</Button>
				))}
				<Button variant="ghost" size="sm" onClick={handleCopy}>
					{copied ? (
						<CheckIcon className="size-4" aria-hidden />
					) : (
						<CopyIcon className="size-4" aria-hidden />
					)}
					{copied ? "Copied" : "Copy prompt"}
				</Button>
			</div>
		</div>
	);
}
